import appConfig from 'src/authConfig';
import dayjs from 'src/utils/dayjs-config';

// Returns the airport location object from app config
export const getAirportLocation = () => ({
    locationId: appConfig.airportLocationId,
    name: appConfig.airportName,
    isAirport: true,
});

// Formats any date to 'YYYY-MM-DD' string
export const formatDateToString = (date) => dayjs(date).format('YYYY-MM-DD');

// Returns true if two dates fall on the same calendar day
export const isSameCalendarDate = (dateA, dateB) =>
    formatDateToString(dateA) === formatDateToString(dateB);

// Returns true if a service date falls within a hotel stay (inclusive of both CheckIn and CheckOut)
export const isDateDuringHotelStay = (serviceDate, hotelCheckIn, hotelCheckOut) => {
    const date = dayjs(serviceDate);
    const checkIn = dayjs(hotelCheckIn);
    const checkOut = dayjs(hotelCheckOut);
    return (date.isSame(checkIn) || date.isAfter(checkIn))
        && (date.isBefore(checkOut) || date.isSame(checkOut));
};

const getHotelDisplayName = (hotel) => hotel.PropertyName || 'Hotel';

// Converts a hotel cart item into a route location reference { locationId, name }
const toHotelLocationRef = (hotel) => ({
    locationId: hotel.LocationId,
    name: getHotelDisplayName(hotel),
});

// Converts a service cart item into a waypoint object
const toServiceWaypoint = (service) => ({
    locationId: service.LocationId,
    name: service.ServiceName || 'Service',
    distance: null,
    duration: null,
});

// Creates an empty route shell with common defaults
const createRouteTemplate = () => ({
    distance: null,
    waypoints: [],
    isAutoGenerated: true,
    searchResults: [],
    selectedUnit: null,
});

const createRouteIdFactory = () => {
    let sequenceNumber = 0;
    return (routeType) => `route_${routeType}_${sequenceNumber++}`;
};

// Generates transfer routes based on hotels and services in the cart
export const generateTransferRoutes = (hotels = [], services = []) => {

    const airport = getAirportLocation();

    const formattedHotel = (h) => ({
        ...h,
        PropertyName: h.Name || 'Hotel',
    });

    const formattedService = (s) => ({
        ...s,
        ServiceName: s.Name || 'Service',
    });

    const formattedHotels = hotels.map(formattedHotel);
    const formattedServices = services.map(formattedService);

    // Only hotels that have a location and dates are valid for route generation
    const hotelsWithLocation = formattedHotels.filter(h => h.LocationId && h.CheckIn && h.CheckOut);
    if (hotelsWithLocation.length === 0) return [];

    const hotelsSortedByCheckIn = [...hotelsWithLocation].sort((hotelA, hotelB) => dayjs(hotelA.CheckIn).diff(dayjs(hotelB.CheckIn)));

    const servicesWithLocation = formattedServices.filter(s => s.LocationId && s.CheckIn);

    // Travel dates are dates where one hotel's CheckOut = next hotel's CheckIn (same day) - checkOut 25/03 Hotel A, checkIn 25/03 Hotel B → 25/03 is a travel date (called transit waypoints)
    // Services on these dates are transit waypoints, not day-trip
    const hotelTravelDates = new Set();
    for (let i = 0; i < hotelsSortedByCheckIn.length - 1; i++) {
        const departingHotel = hotelsSortedByCheckIn[i]; //25/03
        const arrivingHotel = hotelsSortedByCheckIn[i + 1]; //25/03
        if (isSameCalendarDate(departingHotel.CheckOut, arrivingHotel.CheckIn)) {
            hotelTravelDates.add(formatDateToString(departingHotel.CheckOut));
        }
    }

    // Collect location IDs of services that are day-trips (excursions from a hotel)
    // Services on travel dates are excluded — they belong to intermediate routes instead
    const dayTripServiceLocationIds = new Set(
        servicesWithLocation
            .filter(service => {
                if (hotelTravelDates.has(formatDateToString(service.CheckIn))) return false;
                return hotelsSortedByCheckIn.some(hotel =>
                    isDateDuringHotelStay(service.CheckIn, hotel.CheckIn, hotel.CheckOut)
                    && service.LocationId !== hotel.LocationId
                );
            })
            .map(service => service.LocationId)
    );

    // Returns services on a given date that are NOT day-trips and NOT in the excluded locations list
    // Used to find transit waypoints for arrival and intermediate routes
    const getNonDayTripServicesOnDate = (targetDate, excludeLocationIds = []) =>
        servicesWithLocation
            .filter(service =>
                isSameCalendarDate(service.CheckIn, targetDate)
                && !excludeLocationIds.includes(service.LocationId)
                && !dayTripServiceLocationIds.has(service.LocationId)
            )
            .map(toServiceWaypoint);

    const transferRoutes = [];
    const createRouteId = createRouteIdFactory();
    const firstHotel = hotelsSortedByCheckIn[0];
    const lastHotel = hotelsSortedByCheckIn[hotelsSortedByCheckIn.length - 1];

    // Arrival route: Airport → First Hotel
    transferRoutes.push({
        ...createRouteTemplate(),
        routeId: createRouteId('arrival'),
        type: 'arrival',
        fromLocation: airport,
        toLocation: toHotelLocationRef(firstHotel),
        fromDate: firstHotel.CheckIn,
        toDate: firstHotel.CheckIn,
        waypoints: getNonDayTripServicesOnDate(
            firstHotel.CheckIn,
            [airport.locationId, firstHotel.LocationId]
        ),
    });

    // Loop each hotel to find day-trip routes and intermediate routes to the next hotel
    for (let hotelIndex = 0; hotelIndex < hotelsSortedByCheckIn.length; hotelIndex++) {
        const currentHotel = hotelsSortedByCheckIn[hotelIndex];
        const currentHotelLocation = toHotelLocationRef(currentHotel);

        // Find services that are day-trip excursions from this hotel (excursion day meaning: Jetwing → Dutch Museum → Jetwing)
        // (during stay, different location, not on a travel date)
        const dayTripServices = servicesWithLocation.filter(service =>
            isDateDuringHotelStay(service.CheckIn, currentHotel.CheckIn, currentHotel.CheckOut)
            && service.LocationId !== currentHotel.LocationId
            && !hotelTravelDates.has(formatDateToString(service.CheckIn))
        );

        // Group day-trip services by date — one route per excursion day (excursion day meaning: Jetwing → Dutch Museum → Jetwing)
        const dayTripsByDate = new Map();
        dayTripServices.forEach(service => {
            const serviceDate = formatDateToString(service.CheckIn);
            if (!dayTripsByDate.has(serviceDate)) dayTripsByDate.set(serviceDate, []);
            dayTripsByDate.get(serviceDate).push(toServiceWaypoint(service));
        });

        // Day-trip route: Hotel → (Waypoints) → Hotel, one route per excursion day (excursion day meaning: Jetwing → Dutch Museum → Jetwing)
        [...dayTripsByDate.keys()].sort().forEach(excursionDate => {
            transferRoutes.push({
                ...createRouteTemplate(),
                routeId: createRouteId('daytrip'),
                type: 'intermediate',
                fromLocation: currentHotelLocation,
                toLocation: currentHotelLocation,
                fromDate: excursionDate,
                toDate: excursionDate,
                waypoints: dayTripsByDate.get(excursionDate),
            });
        });

        // Intermediate route to next hotel (if not the last hotel)
        const isLastHotel = hotelIndex === hotelsSortedByCheckIn.length - 1;
        if (!isLastHotel) {
            const nextHotel = hotelsSortedByCheckIn[hotelIndex + 1];
            const isDifferentLocation = currentHotel.LocationId !== nextHotel.LocationId;

            // Only create intermediate route if hotels are in different locations
            if (isDifferentLocation) {
                // Collect any services on the travel day as transit waypoints
                const transitLocationExcludeIds = [currentHotel.LocationId, nextHotel.LocationId];
                const transitWaypointMap = new Map();
                [currentHotel.CheckOut, nextHotel.CheckIn].forEach(travelDate => { // Hotel A CheckOut and Hotel B CheckIn are the same day - check both for any services
                    getNonDayTripServicesOnDate(travelDate, transitLocationExcludeIds)
                        .forEach(waypoint => {
                            if (!transitWaypointMap.has(waypoint.locationId)) {
                                transitWaypointMap.set(waypoint.locationId, waypoint);
                            }
                        });
                });

                // Intermediate route: Hotel A → Hotel B
                transferRoutes.push({
                    ...createRouteTemplate(),
                    routeId: createRouteId('intermediate'),
                    type: 'intermediate',
                    fromLocation: currentHotelLocation,
                    toLocation: toHotelLocationRef(nextHotel),
                    fromDate: nextHotel.CheckIn,
                    toDate: nextHotel.CheckIn,
                    waypoints: Array.from(transitWaypointMap.values()),
                });
            }
        }
    }

    // Departure date is CheckOut + 1 day (guest stays through checkout night)
    const actualDepartureDate = formatDateToString(dayjs(lastHotel.CheckOut).add(1, 'day'));

    // Departure route: Last Hotel → Airport (no waypoints — guest is heading to catch a flight)
    transferRoutes.push({
        ...createRouteTemplate(),
        routeId: createRouteId('departure'),
        type: 'departure',
        fromLocation: toHotelLocationRef(lastHotel),
        toLocation: airport,
        fromDate: actualDepartureDate,
        toDate: actualDepartureDate,
        waypoints: []
    });

    return transferRoutes;
};